import productImage0 from '@resources/img/pages/products/cotton-silk-bandanas/icon-1.svg?url';
import productImage1 from '@resources/img/pages/products/cotton-silk-bandanas/icon-3.svg?url';
import productImage2 from '@resources/img/pages/products/twilly-scarf/double-sided.svg?url';
import productImage3 from '@resources/img/pages/products/cotton-silk-bandanas/icon-4.svg?url';
import productImage4 from '@resources/img/pages/products/cotton-silk-bandanas/icon-5.svg?url';
import productImage5 from '@resources/img/pages/products/twilly-scarf/gallery_0.jpg';
import productImage6 from '@resources/img/pages/products/twilly-scarf/gallery_1.jpg';
import productImage7 from '@resources/img/pages/products/twilly-scarf/gallery_2.jpg';
import productImage8 from '@resources/img/pages/products/twilly-scarf/gallery_3.jpg';
import productImage9 from '@resources/img/pages/products/twilly-scarf/gallery_4.jpg';
import measurementsUrl from '@resources/img/pages/products/twilly-scarf/measurement.svg?url';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionSizes from '../../_Sections/SectionSizes';
import SectionPricing from '../../_Sections/SectionPricing';
import ProductSEO from '../../_Sections/ProductSEO';
import tKey from '../../../Common/Utils/tKey';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const bannerEn = new URL('@resources/img/pages/products/twilly-scarf/banner-1-en_CA.png', import.meta.url).href;
const bannerFr = new URL('@resources/img/pages/products/twilly-scarf/banner-1-fr_CA.png', import.meta.url).href;

const PRODUCT_ID = 141;
const VIEW_PRODUCT_ID = 171;

export default function TwillyScarfPage() {
  const [t] = useTranslation('page/products/twilly-scarves');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));

  if (!productData) return null;

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom All-over Print Twilly Scarf | Products')}
      description={t('Design and create your own custom printed twilly scarves featuring your artwork. ✔ Print-on-demand drop shipping ✔ No minimums ✔ Made in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#F4E4F4"
      bgStyle={{
        backgroundPosition: 'bottom right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'min(55%, 850px)',
      }}
      fullPageBg
      mobileImageStyle={{
        display: 'block',
        width: 'auto',
        maxWidth: '100%',
        maxHeight: 'min(60svh, 520px)',
        marginLeft: 'auto',
        WebkitMaskImage: 'linear-gradient(to bottom, #000 68%, transparent 98%)',
        maskImage: 'linear-gradient(to bottom, #000 68%, transparent 98%)',
      }}
      tag={t('All-over print')}
      title={t('Twilly Scarf')}
      text={t('Luxury looks good on you! Turn your artwork into a custom all-over print silk twilly scarf! Made on-demand from 14 momme mulberry silk twill, this versatile all-over print accessory can feature a different design on each side. Wear it as a hair wrap, neck scarf, wrist tie, purse decoration, and more.')}
      createUrl={productData.get('url_slug')}
      img={locale === 'fr_CA' ? bannerFr : bannerEn}
      alt={t('Model wearing a custom printed Twilly Scarf')}
      details={[
        {
          img: productImage0,
          alt: t('Silk fabric icon'),
          text: t('Made from luxurious 14 momme mulberry silk twill'),
        },
        {
          img: productImage1,
          alt: t('Size icon'),
          text: t('Generous length for more styling options'),
        },
        {
          img: productImage2,
          alt: t('Double-sided design icon'),
          text: t('Double-sided design lets you feature different artwork on the front and back'),
        },
        {
          img: productImage3,
          alt: t('Durable ink icon'),
          text: t('Vibrant all-over print using reactive inks for rich, durable colour'),
        },
        {
          img: productImage4,
          alt: t('Canada icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />
    <SectionGallery images={[
      productImage5,
      productImage6,
      productImage7,
      productImage8,
      productImage9,
    ]} />
    <SectionDropship
      title={<Trans t={t} i18nKey={tKey('<yellow>Drop ship</yellow> your Twilly Scarf <pink>on-demand</pink>')}
        components={{ yellow: <span className="ty" />, pink: <span className="tp" /> }} />}
      text={t('Bring your artwork to life on silk, without stocking inventory. Sell your custom Twilly Scarf in your eCommerce store and choose your profit margin.')}
    />
    <SectionPricing productId={PRODUCT_ID} />
    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Twilly Scarf Sizing')}
      text={<Trans t={t} i18nKey={tKey('One size: <length/> long (tip to tip) and <width/> wide. Silk twilly scarves are made by hand. Please allow for small variations in size.')}
        components={{
          length: <SizeDisplay sizes={[103]} sourceUnit="cm" displayType="physical" />,
          width: <SizeDisplay sizes={[6]} sourceUnit="cm" displayType="physical" />,
        }} />}
    >
      <Box
        component="svg"
        viewBox="0 -40 2100 270"
        role="img"
        aria-label={t('Twilly Scarf measurements from tip to tip and across its width')}
        sx={{
          display: 'block',
          width: '100%',
          height: 'auto',
          mt: '60px',
          '& text': {
            fill: 'currentColor',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 500,
            fontSize: { xs: 80, sm: 60, md: 49 },
          },
        }}
      >
        <image aria-hidden href={measurementsUrl} width="1932.794" height="213.054" />
        <text x="852.5" y="25" textAnchor="middle" dominantBaseline="middle">
          <SizeDisplay sizes={[103]} sourceUnit="cm" displayType="physical" />
        </text>
        <text x="1790" y="170" dominantBaseline="middle">
          <SizeDisplay sizes={[6]} sourceUnit="cm" displayType="physical" />
        </text>
      </Box>
    </SectionSizes>
    <SectionBranding
      title={t('Brand your Twilly Scarf')}
      text={t('Add custom artist cards or stickers to your packages to share your story and build your brand.')}
    />
    <SectionPairings
      text={t('Pair your Twilly Scarf with these great products to create your own unique style.')}
      productSlugs={['silk-charmeuse-scarves', 'vegan-leather-tote-bag', 'vegan-leather-crossbody-purse']}
    />
  </div>;
}
